/**
 * KioskWideFormatDept - Wide Format Department Kiosk View
 * 
 * Features:
 * - No login required (uses kiosk access key in URL)
 * - Auto-refreshes every 60 seconds
 * - Left column: Wide Format Prepress jobs
 * - Right area: Snake view for Wide Format jobs (fills vertically first)
 * - Summary bar with due date counts
 * - Color-coded urgency (Overdue/Today/This Week)
 * - Shows operator name on active jobs
 * 
 * Usage: /kiosk/wide-format-dept?key=YOUR_KIOSK_KEY
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, Maximize2, Minimize2, AlertTriangle, Printer,
  Wifi, WifiOff, Package, Clock, Layers, Play, User, AlertCircle,
  Calendar, CalendarClock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge colors (light theme)
const STATUS_COLORS = {
  'Prepress_Wide Format': 'bg-blue-100 text-blue-700 border border-blue-300',
  'Wide Format': 'bg-orange-100 text-orange-700 border border-orange-300',
  'Queued': 'bg-gray-200 text-gray-700',
  'Ready': 'bg-blue-100 text-blue-700',
  'In Production': 'bg-green-100 text-green-700',
  'Paused': 'bg-amber-100 text-amber-700',
  'Blocked': 'bg-red-100 text-red-700',
  'Complete': 'bg-emerald-100 text-emerald-700',
};

const KioskWideFormatDept = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]); // Now stores full job tracking info

  // Get kiosk key from URL
  const urlParams = new URLSearchParams(window.location.search);
  const kioskKey = urlParams.get('key') || '';

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Auto-enter fullscreen on load
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    setTimeout(() => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }, 1000);
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    if (!kioskKey) {
      setError('Missing kiosk access key. Add ?key=YOUR_KEY to URL');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/kiosk/wide-format-dept?key=${kioskKey}&limit=200`);
      setJobs(response.data.jobs || []);
      setIsConnected(true);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setIsConnected(false);
      if (err.response?.status === 401) {
        setError('Invalid kiosk access key');
      }
    } finally {
      setLoading(false);
    }
  }, [kioskKey]);

  // Fetch active tracking with operator info
  const fetchActiveTracking = useCallback(async () => {
    if (!kioskKey) return;
    
    try {
      const response = await axios.get(`${API}/kiosk/active-tracking?key=${kioskKey}`);
      setActiveJobs(response.data.active_jobs || []);
    } catch (err) {
      console.error('Failed to fetch active tracking:', err);
    }
  }, [kioskKey]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    fetchJobs();
    fetchActiveTracking();
    const jobsInterval = setInterval(fetchJobs, 60000);
    const trackingInterval = setInterval(fetchActiveTracking, 5000);
    return () => {
      clearInterval(jobsInterval);
      clearInterval(trackingInterval);
    };
  }, [fetchJobs, fetchActiveTracking]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get active job info including operator name
  const getActiveJobInfo = (jobId) => {
    return activeJobs.find(aj => aj.job_id === jobId);
  };

  // Check job urgency level
  const getJobUrgency = (job) => {
    if (!job.date_due || job.date_due === '0000-00-00') return 'none';
    
    const dueDate = new Date(job.date_due);
    dueDate.setHours(23, 59, 59, 999);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    
    if (dueDate < today) return 'overdue';
    if (dueDate < tomorrow) return 'today';
    if (dueDate < endOfWeek) return 'this-week';
    return 'future';
  };

  // Get urgency styles
  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'overdue':
        return {
          card: 'bg-red-50 border-red-400 ring-2 ring-red-200',
          badge: 'bg-red-500 text-white',
          text: 'OVERDUE'
        };
      case 'today':
        return {
          card: 'bg-amber-50 border-amber-400 ring-2 ring-amber-200',
          badge: 'bg-amber-500 text-white',
          text: 'DUE TODAY'
        };
      case 'this-week':
        return {
          card: 'bg-orange-50 border-orange-300',
          badge: 'bg-orange-400 text-white',
          text: 'THIS WEEK'
        };
      default:
        return {
          card: 'bg-white border-gray-200',
          badge: 'bg-gray-100 text-gray-700',
          text: null
        };
    }
  };

  // Calculate summary counts
  const getSummaryCounts = () => {
    const counts = { overdue: 0, today: 0, thisWeek: 0, total: jobs.length };
    
    jobs.forEach(job => {
      const urgency = getJobUrgency(job);
      if (urgency === 'overdue') counts.overdue++;
      else if (urgency === 'today') counts.today++;
      else if (urgency === 'this-week') counts.thisWeek++;
    });
    
    return counts;
  };

  // Get prepress jobs sorted by due date
  const getPrepressJobs = () => {
    return jobs
      .filter(job => job.printlogic_status === 'Prepress_Wide Format')
      .sort((a, b) => {
        const dateA = a.date_due && a.date_due !== '0000-00-00' ? new Date(a.date_due) : new Date('9999-12-31');
        const dateB = b.date_due && b.date_due !== '0000-00-00' ? new Date(b.date_due) : new Date('9999-12-31');
        return dateA - dateB;
      });
  };

  // Get wide format jobs sorted by due date
  const getWideFormatJobs = () => {
    return jobs
      .filter(job => job.printlogic_status === 'Wide Format')
      .sort((a, b) => {
        const dateA = a.date_due && a.date_due !== '0000-00-00' ? new Date(a.date_due) : new Date('9999-12-31');
        const dateB = b.date_due && b.date_due !== '0000-00-00' ? new Date(b.date_due) : new Date('9999-12-31');
        return dateA - dateB;
      });
  };

  // Job Card Component for Wide Format (main area)
  const JobCard = ({ job, index }) => {
    const urgency = getJobUrgency(job);
    const urgencyStyles = getUrgencyStyles(urgency);
    const activeInfo = getActiveJobInfo(job.job_id);
    const isActive = !!activeInfo;

    return (
      <div
        className={`
          p-3 rounded-lg border-2 transition-all shadow-sm
          ${isActive 
            ? 'bg-green-50 border-green-400 ring-2 ring-green-300 shadow-lg' 
            : urgencyStyles.card
          }
        `}
      >
        {/* Urgency Banner */}
        {urgencyStyles.text && !isActive && (
          <div className={`${urgencyStyles.badge} text-[10px] font-black px-2 py-0.5 rounded mb-2 inline-block`}>
            {urgencyStyles.text}
          </div>
        )}

        {/* Active Operator Banner */}
        {isActive && (
          <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 flex items-center gap-1 animate-pulse">
            <User className="w-3 h-3" />
            <span>{activeInfo.operator_name || 'Active'}</span>
          </div>
        )}

        {/* Header Row */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
              {index + 1}
            </span>
            <span className={`text-lg font-black ${isActive ? 'text-green-700' : urgency === 'overdue' ? 'text-red-700' : 'text-blue-700'}`}>
              #{job.job_id}
            </span>
            {isActive && <Play className="w-4 h-4 text-green-600 animate-pulse" />}
            {urgency === 'overdue' && !isActive && <AlertTriangle className="w-4 h-4 text-red-500" />}
          </div>
          {job.date_due && job.date_due !== '0000-00-00' && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-bold ${urgencyStyles.badge}`}>
              <Clock className="w-4 h-4" />
              <span>{new Date(job.date_due).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}</span>
            </div>
          )}
        </div>

        {/* Customer */}
        <p className="font-bold text-gray-800 text-sm mb-1">{job.customer}</p>
        
        {/* Product */}
        <p className="text-gray-600 text-xs mb-2">{job.product}</p>

        {/* Material */}
        {job.materials && (
          <div className="p-2 bg-orange-50 rounded border border-orange-200 mb-2">
            <div className="flex items-start gap-1 text-orange-700 text-xs">
              <Layers className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="font-medium">{job.materials}</span>
            </div>
          </div>
        )}

        {/* Printing Process */}
        {job.printing && (
          <div className="p-2 bg-blue-50 rounded border border-blue-200 mb-2">
            <div className="flex items-start gap-1 text-blue-700 text-xs">
              <Printer className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-2">{job.printing}</span>
            </div>
          </div>
        )}

        {/* Footer Row */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            <span className="font-bold text-gray-700">Qty: {job.quantity?.toLocaleString() || 1}</span>
          </div>
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATUS_COLORS[job.board_status] || 'bg-gray-100 text-gray-600'}`}>
            {job.board_status}
          </span>
        </div>
      </div>
    );
  };

  // Compact Job Card for Prepress column
  const PrepressJobCard = ({ job }) => {
    const urgency = getJobUrgency(job);
    const urgencyStyles = getUrgencyStyles(urgency);
    const activeInfo = getActiveJobInfo(job.job_id);
    const isActive = !!activeInfo;

    return (
      <div
        className={`
          p-2 rounded-lg border-2 transition-all text-xs
          ${isActive 
            ? 'bg-green-50 border-green-400 shadow-md' 
            : urgency === 'overdue'
              ? 'bg-red-50 border-red-300'
              : urgency === 'today'
                ? 'bg-amber-50 border-amber-300'
                : 'bg-blue-50 border-blue-200'
          }
        `}
      >
        {/* Urgency/Active Banner */}
        {isActive ? (
          <div className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded mb-1 flex items-center gap-0.5">
            <User className="w-3 h-3" />
            <span className="truncate">{activeInfo.operator_name || 'Active'}</span>
          </div>
        ) : urgencyStyles.text && (
          <div className={`${urgencyStyles.badge} text-[9px] font-black px-1.5 py-0.5 rounded mb-1 inline-block`}>
            {urgencyStyles.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-1 mb-1">
          <div className="flex items-center gap-1">
            <span className={`text-sm font-black ${isActive ? 'text-green-700' : urgency === 'overdue' ? 'text-red-700' : 'text-blue-700'}`}>
              #{job.job_id}
            </span>
            {isActive && <Play className="w-3 h-3 text-green-600 animate-pulse" />}
            {urgency === 'overdue' && !isActive && <AlertTriangle className="w-3 h-3 text-red-500" />}
          </div>
          {job.date_due && job.date_due !== '0000-00-00' && (
            <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${urgencyStyles.badge}`}>
              <Clock className="w-3 h-3" />
              <span>{new Date(job.date_due).toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })}</span>
            </div>
          )}
        </div>

        {/* Customer */}
        <p className="font-semibold text-gray-800 truncate">{job.customer}</p>
        
        {/* Product */}
        <p className="text-gray-500 truncate text-[10px] mb-1">{job.product}</p>

        {/* Material */}
        {job.materials && (
          <div className="p-1 bg-orange-50 rounded border border-orange-200 mb-1">
            <div className="flex items-center gap-0.5 text-orange-700 text-[10px]">
              <Layers className="w-3 h-3 flex-shrink-0" />
              <span className="truncate font-medium">{job.materials}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-[10px] text-gray-500 pt-1 border-t border-gray-100">
          <span className="font-bold text-gray-700">Qty: {job.quantity?.toLocaleString() || 1}</span>
          <span className={`px-1 py-0.5 rounded font-bold ${STATUS_COLORS[job.board_status] || 'bg-gray-100 text-gray-600'}`}>
            {job.board_status}
          </span>
        </div>
      </div>
    );
  };

  // Error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Kiosk Error</h1>
          <p className="text-xl text-red-600 mb-6">{error}</p>
          <p className="text-gray-500">
            URL format: /kiosk/wide-format-dept?key=YOUR_KIOSK_KEY
          </p>
        </div>
      </div>
    );
  }

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-gray-800">Loading Wide Format Department...</p>
          <p className="text-gray-500 mt-2">Kiosk Mode</p>
        </div>
      </div>
    );
  }

  const prepressJobs = getPrepressJobs();
  const wideFormatJobs = getWideFormatJobs();
  const counts = getSummaryCounts();

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 shadow-sm px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center shadow">
              <Printer className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-800 tracking-tight">
                WIDE FORMAT DEPARTMENT
              </h1>
              <p className="text-xs text-gray-500">
                {jobs.length} jobs • Sorted by due date
              </p>
            </div>
          </div>

          {/* Summary Counts */}
          <div className="flex items-center gap-2">
            {counts.overdue > 0 && (
              <div className="flex items-center gap-1 bg-red-500 text-white px-3 py-1.5 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                <span className="font-bold">{counts.overdue}</span>
                <span className="text-xs">Overdue</span>
              </div>
            )}
            {counts.today > 0 && (
              <div className="flex items-center gap-1 bg-amber-500 text-white px-3 py-1.5 rounded-lg">
                <Calendar className="w-4 h-4" />
                <span className="font-bold">{counts.today}</span>
                <span className="text-xs">Due Today</span>
              </div>
            )}
            {counts.thisWeek > 0 && (
              <div className="flex items-center gap-1 bg-orange-400 text-white px-3 py-1.5 rounded-lg">
                <CalendarClock className="w-4 h-4" />
                <span className="font-bold">{counts.thisWeek}</span>
                <span className="text-xs">This Week</span>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${
              isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span className="font-medium">{isConnected ? 'Online' : 'Offline'}</span>
            </div>

            {/* Current Time */}
            <div className="text-2xl font-mono font-bold text-gray-800">
              {currentTime.toLocaleTimeString('en-IE', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              })}
            </div>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              {isFullscreen ? (
                <Minimize2 className="w-5 h-5 text-gray-700" />
              ) : (
                <Maximize2 className="w-5 h-5 text-gray-700" />
              )}
            </button>

            {/* Refresh Button */}
            <button
              onClick={fetchJobs}
              className="p-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 p-3 overflow-hidden">
        <div className="flex gap-3 h-full">
          
          {/* Left Column - Wide Format Prepress */}
          <div className="w-64 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-blue-600 px-4 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-black text-white uppercase">PREPRESS</h2>
                <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                  {prepressJobs.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-2 overflow-y-auto">
              {prepressJobs.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No jobs</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {prepressJobs.map((job) => (
                    <PrepressJobCard key={job.job_id} job={job} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Area - Wide Format Snake View */}
          <div className="flex-1 flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="bg-orange-500 px-4 py-2 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-black text-white uppercase">WIDE FORMAT</h2>
                <span className="bg-white/20 text-white text-sm font-bold px-2 py-0.5 rounded-full">
                  {wideFormatJobs.length}
                </span>
              </div>
            </div>
            <div className="flex-1 p-3 overflow-hidden">
              {wideFormatJobs.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No jobs</p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col flex-wrap gap-3 content-start">
                  {wideFormatJobs.map((job, index) => (
                    <div key={job.job_id} className="w-72">
                      <JobCard job={job} index={index} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default KioskWideFormatDept;
